import React from "react";
import { Box } from "@mui/joy";

interface PostcardProps {
  destination: string;
  variant: PostcardVariant;
  style?: React.CSSProperties;
}

export type PostcardVariant =
  | "paris"
  | "tokyo"
  | "santorini"
  | "machu-picchu"
  | "new-york"
  | "marrakech"
  | "bali"
  | "rio";

interface PostcardTheme {
  skyGradient: string;
  accentColor: string;
  landmark: React.ReactNode;
}

const POSTCARD_THEMES: Record<PostcardVariant, PostcardTheme> = {
  paris: {
    skyGradient: "linear-gradient(180deg, #FF9A76 0%, #FECDA6 60%, #EDE8DC 100%)",
    accentColor: "#C8912A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60 5L45 55H75L60 5Z" fill="#15253F" opacity="0.7" />
        <rect x="56" y="55" width="8" height="20" fill="#15253F" opacity="0.7" />
        <rect x="20" y="60" width="80" height="2" fill="#15253F" opacity="0.3" />
        <rect x="10" y="65" width="25" height="12" rx="1" fill="#15253F" opacity="0.25" />
        <rect x="85" y="62" width="20" height="15" rx="1" fill="#15253F" opacity="0.25" />
        <rect x="38" y="63" width="15" height="14" rx="1" fill="#15253F" opacity="0.2" />
      </svg>
    ),
  },
  tokyo: {
    skyGradient: "linear-gradient(180deg, #FFB7C5 0%, #FDE2E4 50%, #EDE8DC 100%)",
    accentColor: "#C8912A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M60 10L55 25H65L60 10Z" fill="#15253F" opacity="0.6" />
        <path d="M55 25L48 40H72L65 25H55Z" fill="#15253F" opacity="0.5" />
        <path d="M48 40L40 58H80L72 40H48Z" fill="#15253F" opacity="0.45" />
        <rect x="57" y="58" width="6" height="15" fill="#15253F" opacity="0.5" />
        <circle cx="30" cy="20" r="4" fill="#FFB7C5" opacity="0.6" />
        <circle cx="85" cy="25" r="3" fill="#FFB7C5" opacity="0.5" />
        <circle cx="25" cy="30" r="2.5" fill="#FFB7C5" opacity="0.4" />
        <circle cx="90" cy="15" r="2" fill="#FFB7C5" opacity="0.5" />
      </svg>
    ),
  },
  santorini: {
    skyGradient: "linear-gradient(180deg, #5B9BB5 0%, #87CEEB 50%, #EDE8DC 100%)",
    accentColor: "#5B9BB5",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="15" y="35" width="22" height="38" rx="2" fill="white" opacity="0.9" />
        <ellipse cx="26" cy="30" rx="8" ry="6" fill="#5B9BB5" opacity="0.7" />
        <rect x="45" y="28" width="25" height="45" rx="2" fill="white" opacity="0.85" />
        <ellipse cx="57.5" cy="23" rx="9" ry="7" fill="#5B9BB5" opacity="0.75" />
        <rect x="78" y="40" width="20" height="33" rx="2" fill="white" opacity="0.8" />
        <ellipse cx="88" cy="36" rx="7" ry="5" fill="#5B9BB5" opacity="0.65" />
        <rect x="0" y="70" width="120" height="10" fill="#22756A" opacity="0.15" />
      </svg>
    ),
  },
  "machu-picchu": {
    skyGradient: "linear-gradient(180deg, #87CEEB 0%, #98D4A5 40%, #22756A 100%)",
    accentColor: "#22756A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 75L30 25L60 55L90 15L120 50V80H0V75Z" fill="#22756A" opacity="0.4" />
        <path d="M0 80L25 45L50 65L80 30L120 60V80H0Z" fill="#22756A" opacity="0.3" />
        <rect x="42" y="50" width="6" height="4" fill="#C8912A" opacity="0.5" />
        <rect x="50" y="48" width="8" height="6" fill="#C8912A" opacity="0.45" />
        <rect x="60" y="50" width="6" height="4" fill="#C8912A" opacity="0.4" />
        <path d="M45 50L48 46H58L61 50" stroke="#C8912A" strokeWidth="0.8" opacity="0.5" fill="none" />
      </svg>
    ),
  },
  "new-york": {
    skyGradient: "linear-gradient(180deg, #15253F 0%, #FF6B4A 40%, #FECDA6 100%)",
    accentColor: "#15253F",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="30" width="12" height="48" fill="#15253F" opacity="0.6" />
        <rect x="28" y="20" width="10" height="58" fill="#15253F" opacity="0.55" />
        <rect x="44" y="35" width="14" height="43" fill="#15253F" opacity="0.5" />
        <rect x="64" y="10" width="8" height="68" fill="#15253F" opacity="0.65" />
        <path d="M68 10L68 2L66 10" fill="#15253F" opacity="0.6" />
        <rect x="78" y="25" width="12" height="53" fill="#15253F" opacity="0.5" />
        <rect x="96" y="38" width="14" height="40" fill="#15253F" opacity="0.45" />
      </svg>
    ),
  },
  marrakech: {
    skyGradient: "linear-gradient(180deg, #FF9A76 0%, #E8A87C 50%, #D4A373 100%)",
    accentColor: "#C8912A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30 75V35C30 35 30 20 45 20C60 20 60 35 60 35V75" fill="#D4A373" opacity="0.5" />
        <path d="M60 75V35C60 35 60 20 75 20C90 20 90 35 90 35V75" fill="#D4A373" opacity="0.4" />
        <rect x="55" y="15" width="2" height="60" fill="#C8912A" opacity="0.6" />
        <circle cx="56" cy="12" r="3" fill="#C8912A" opacity="0.5" />
        <path d="M15 55C15 55 15 45 25 45C35 45 35 55 35 55" fill="#D4A373" opacity="0.3" />
        <rect x="85" y="45" width="20" height="30" rx="1" fill="#D4A373" opacity="0.35" />
      </svg>
    ),
  },
  bali: {
    skyGradient: "linear-gradient(180deg, #87CEEB 0%, #98D4A5 30%, #4CAF50 100%)",
    accentColor: "#22756A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 75C20 75 20 55 30 55C40 55 40 65 50 60C60 55 60 45 70 50C80 55 80 65 90 60C100 55 110 65 120 70V80H0V75Z" fill="#22756A" opacity="0.35" />
        <path d="M10 80C20 80 20 65 30 65C40 65 45 70 55 68C65 66 70 58 80 62C90 66 95 72 105 70C115 68 120 75 120 75V80H10Z" fill="#22756A" opacity="0.25" />
        <rect x="55" y="20" width="10" height="35" fill="#8B4513" opacity="0.4" />
        <ellipse cx="60" cy="18" rx="18" ry="12" fill="#22756A" opacity="0.5" />
        <ellipse cx="60" cy="15" rx="12" ry="8" fill="#22756A" opacity="0.4" />
      </svg>
    ),
  },
  rio: {
    skyGradient: "linear-gradient(180deg, #FFB347 0%, #FF6B4A 40%, #FECDA6 100%)",
    accentColor: "#22756A",
    landmark: (
      <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M30 75L60 20L90 75" fill="#22756A" opacity="0.35" />
        <path d="M60 20V28" stroke="#15253F" strokeWidth="2" opacity="0.6" />
        <line x1="52" y1="26" x2="60" y2="22" stroke="#15253F" strokeWidth="1.5" opacity="0.5" />
        <line x1="68" y1="26" x2="60" y2="22" stroke="#15253F" strokeWidth="1.5" opacity="0.5" />
        <path d="M0 65C10 60 20 70 35 68C50 66 55 72 70 70C85 68 95 65 120 70V80H0V65Z" fill="#5B9BB5" opacity="0.3" />
        <path d="M0 75L15 55L30 70" fill="#22756A" opacity="0.25" />
        <path d="M90 75L105 50L120 70" fill="#22756A" opacity="0.25" />
      </svg>
    ),
  },
};

const POSTCARD_WIDTH = 180;
const POSTCARD_HEIGHT = 130;

export const Postcard: React.FC<PostcardProps> = ({
  destination,
  variant,
  style,
}) => {
  const theme = POSTCARD_THEMES[variant];

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: POSTCARD_WIDTH,
        height: POSTCARD_HEIGHT,
        borderRadius: "8px",
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
        flexShrink: 0,
        position: "relative",
        background: "#fff",
        ...style,
      }}
    >
      {/* Sky / landmark area */}
      <Box
        sx={{
          width: "100%",
          height: "70%",
          background: theme.skyGradient,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          overflow: "hidden",
          "& svg": {
            width: "100%",
            height: "100%",
          },
        }}
      >
        {theme.landmark}
      </Box>

      {/* Bottom strip */}
      <Box
        sx={{
          width: "100%",
          height: "30%",
          background: "#FDFBF7",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          boxSizing: "border-box",
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "11px",
            fontWeight: 600,
            color: "#15253F",
            letterSpacing: "0.02em",
          }}
        >
          {destination}
        </Box>
        {/* Stamp mark */}
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            border: `1.5px solid ${theme.accentColor}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.6,
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: "7px",
              fontWeight: 700,
              color: theme.accentColor,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            L
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
